import React, { useState } from 'react'

import { faCaretDown } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Backdrop, Box, Divider, Popover } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import cn from 'classnames'
import PropTypes from 'prop-types'

import { useResponsiveBreakpoint } from 'common/use-responsive-breakpoint'
import { AuthShieldComponent, AuthShieldHide } from 'components/AuthShield'
import { FEATURE_NAMES } from 'components/ConfiguredDynamicFeatureProvider/features'
import DynamicFeature from 'components/DynamicFeatureProvider/DynamicFeature'

import { propsUnauthed } from '../PageTabs'
import LogoutButton from '../components/LogoutButton'
import MainTabItem from '../components/MainTabItem'
import PagesList from '../components/PagesList'
import PendingInvites from '../components/PendingInvites'
import ProfilePreview from '../components/ProfilePreview'
import ApplyWithScoir from './ApplyWithScoir'
import NotificationAvatar from './NotificationAvatar'
import { useCloseOnChanges } from './use-close-on-changes'

const useStyles = makeStyles({
  menuPaper: {
    width: '256px',
  },
  menu: {
    marginTop: '68px',
    marginLeft: '-4px',
  },
  floatingSidebar: {
    position: 'relative',
    zIndex: 12,
  },
})

const PROF_URL = `${import.meta.env.BASE_URL}/profile`
const PROF_REGEX = new RegExp(PROF_URL)
const TRIGGER_ID = 'main-nav-floating-sidebar-trigger'
const MENU_ID = 'me-menu'

/**
 * This is the dropdown "floating" version of the sidebar for top-navigation
 * @param {object} props
 * @prop {bool} open
 * @prop {func} onOpen
 * @prop {func} onClose
 * @returns Floating Sidebar Component
 */
const Floating = ({ open, onOpen, onClose, pathname, queryParams }) => {
  const classes = useStyles()
  const isOnProfile = PROF_REGEX.test(pathname)
  const isBelow1190 = useResponsiveBreakpoint(1190)

  useCloseOnChanges(open, onClose, [pathname, queryParams])

  const [anchorEl, setAnchorEl] = useState(null)
  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget)
    onOpen()
  }
  const handleClose = () => {
    setAnchorEl(null)
    onClose()
  }

  return (
    <div className={cn(classes.floatingSidebar, 'e2e-me-menu-item')}>
      <AuthShieldComponent
        component={MainTabItem}
        id={TRIGGER_ID}
        active={open || isOnProfile}
        onClick={handleOpen}
        haspopup="true"
        aria-haspopup="true"
        label="Me"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={open ? MENU_ID : undefined}
        propsAuthed={{
          title: (
            <span>
              Me&nbsp;
              <FontAwesomeIcon
                className={classes.activeIcon}
                icon={faCaretDown}
              />
            </span>
          ),
        }}
        propsUnauthed={{
          title: <span>Me</span>,
          ...propsUnauthed,
        }}
      >
        <AuthShieldHide hideUnauthed>
          <NotificationAvatar />
        </AuthShieldHide>
      </AuthShieldComponent>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        className={classes.menu}
        classes={{ paper: cn(classes.menuPaper, 'e2e-me-menu-dropdown') }}
        PaperProps={{ id: MENU_ID, 'aria-labelledby': TRIGGER_ID }}
      >
        <Backdrop
          open={open}
          onClick={handleClose}
          invisible
        />
        <ProfilePreview />
        <PendingInvites />
        <Divider />
        <DynamicFeature name={FEATURE_NAMES.MyColleges}>
          {isBelow1190 && (
            <Box mb={0.5}>
              <ApplyWithScoir />
              <Divider />
            </Box>
          )}
        </DynamicFeature>
        <PagesList pathname={pathname} />
        <Box mt={0.5}>
          <Divider />
        </Box>
        <LogoutButton />
      </Popover>
    </div>
  )
}

Floating.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  pathname: PropTypes.string,
  queryParams: PropTypes.string,
}

export default Floating
